<template>
    <div>
        <div
            v-if="isIntegratedEnergyStorageSystem"
            class="avoid-page-break-inside"
            data-test-role="integrated-energy-storage-div">
            <hr class="m-0 mb-3" />
            <fieldset class="mb-3">
                <legend class="h6 mb-0">
                    <span class="d-inline-flex align-items-baseline col-12 col-lg-11 mb-0">
                        <div class="mb-2">
                            <span>{{ integratedEnergyStorageLabel }}</span>
                            <pc-button
                                v-if="showDeleteIntegratedStorageButton"
                                v-bind:disabled="readonly"
                                class="mx-2 p-0 border-0"
                                style="line-height: inherit"
                                data-test-role="delete-integrated-energy-storage"
                                link
                                v-on:click="onDelete">
                                {{ deleteIntegratedEnergyStorageButtonLabel }}
                            </pc-button>
                        </div>
                    </span>
                </legend>
                <energy-storage-model-picker
                    ref="iesModelPicker"
                    class="mb-2"
                    v-bind:manufacturers="parentEquipmentManufacturers"
                    v-bind:load-models="loadParentEquipmentModels"
                    v-bind:spec="energystoragesubsystem"
                    v-bind:equipment-type="parentEquipmentType"
                    v-bind:readonly="readonly"
                    v-on:copy-component="onCopySubsystemParentClicked" />
            </fieldset>
        </div>
        <div
            v-if="isInverterAndBattery"
            class="avoid-page-break-inside"
            data-test-role="inverter-div">
            <hr class="m-0 mb-3" />
            <fieldset class="mb-3">
                <legend class="h6 mb-0">
                    <span class="d-inline-flex align-items-baseline col-12 col-lg-11 mb-0">
                        <div class="mb-2">
                            <span>{{ inverterLabel }}</span>
                            <pc-button
                                v-if="showDeleteInverterButton"
                                v-bind:disabled="readonly"
                                class="mx-2 p-0 border-0"
                                style="line-height: inherit"
                                data-test-role="delete-inverter"
                                link
                                v-on:click="onDelete">
                                {{ deleteInverterButtonLabel }}
                            </pc-button>
                        </div>
                        <div
                            v-if="!readonly"
                            class="ms-auto">
                            <pc-loader
                                small
                                v-bind:loading="copyingInvBattSubsystem">
                                <pc-button-icon
                                    v-bind:disabled="copyingInvBattSubsystem"
                                    v-bind:link="cloneButtonConfig.link"
                                    v-bind:style="cloneButtonConfig.style"
                                    v-bind:readonly="readonly"
                                    v-bind:icon="cloneButtonConfig.iconName"
                                    v-on:click="onCopyInvBattSubsystemClicked">
                                    {{ subsystemCloneButtonLabel }}
                                </pc-button-icon>
                            </pc-loader>
                        </div>
                    </span>
                </legend>
                <energy-storage-model-picker
                    ref="invBattModelPicker"
                    class="mb-3"
                    v-bind:manufacturers="parentEquipmentManufacturers"
                    v-bind:load-models="loadParentEquipmentModels"
                    v-bind:spec="energystoragesubsystem"
                    v-bind:equipment-type="parentEquipmentType"
                    v-bind:readonly="readonly"
                    v-on:copy-component="onCopySubsystemParentClicked" />
                <div v-bind:class="responsiveClasses.subsystemWrapper">
                    <transition-group
                        tag="span"
                        v-bind:css="false"
                        v-on:enter="enter"
                        v-on:leave="leave">
                        <energyStorageEquipmentGroup
                            v-for="childItem in energystoragesubsystem.childGroups"
                            v-bind:ref="childItem.id"
                            v-bind:key="childItem.id"
                            v-bind:equipment-item="childItem"
                            v-bind:equipment-type="childItem.equipmentType"
                            v-bind:parent-id="energystoragesubsystem.id"
                            v-bind:readonly="readonly"
                            v-on:copy-equipmentgroup="copyEquipmentGroup" />
                    </transition-group>
                    <validation-render-helper
                        v-if="needsChildGroups"
                        v-bind:validation-error="[inverterRequiresBatteryErrorMessage]"
                        class="mt-2" />
                    <pc-button
                        v-bind:disabled="readonly"
                        data-test-role="add-battery-btn"
                        v-on:click="onAddEmptyBattery">
                        {{ addBatteryButtonLabel }}
                    </pc-button>
                </div>
            </fieldset>
        </div>
    </div>
</template>

<script>
    import energyStorageEquipmentGroup from "./energyStorageEquipmentGroup.vue";
    import energyStorageModelPicker from "./energyStorageModelPicker.vue";
    import validationRenderHelper from "Validation/_validation-error-helper.vue";
    import { SLIDE_DOWN_UP } from "Util/animations";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./energystoragesubsystem.i18n";
    import { extendEquipmentManufList, extendEquipmentModelList } from "../equipment/equipmentUtil";
    import { COMPONENT_TYPE } from "Types/componentTypes";
    import {
        getParentEquipmentTypeFromSubsystemType,
        ENERGY_STORAGE_SUBSYSTEM_TYPE
    } from "Forms/Components/ProjectForm/ProjectElements/energyStorageSystemElementController";
    import { getResponsiveClassMap } from "Responsive/manager";

    const responsiveConfig = {
        subsystemWrapper: {
            ms: { xs: 2, sm: 5 }
        }
    };

    export default {
        components: { energyStorageModelPicker, energyStorageEquipmentGroup, validationRenderHelper },
        inject: {
            energyStorageElement: {
                from: "energyStorageElement"
            },
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            energystoragesubsystem: { type: Object, required: true },
            readonly: { type: Boolean, required: false, default: false }
        },
        emits: ["copy-invbatt-subsystem", "copy-subsystem-parent"],
        data: function () {
            const cloneButtonConfig = {
                link: true,
                style: {
                    textDecoration: "none"
                },
                iconName: "copy"
            };
            return {
                copyingInvBattSubsystem: false,
                cloneButtonConfig
            };
        },
        computed: {
            isIntegratedEnergyStorageSystem() {
                return this.subsystemType === ENERGY_STORAGE_SUBSYSTEM_TYPE.IntegratedEnergyStorageSystem;
            },
            isInverterAndBattery() {
                return this.subsystemType === ENERGY_STORAGE_SUBSYSTEM_TYPE.InverterAndBattery;
            },
            subsystemType() {
                return this.energystoragesubsystem.subsystemType;
            },
            parentEquipmentType() {
                return getParentEquipmentTypeFromSubsystemType(this.subsystemType);
            },
            parentEquipmentManufacturers() {
                return this.getEquipmentManufacturers(this.parentEquipmentType);
            },
            needsChildGroups() {
                return (
                    this.isInverterAndBattery &&
                    this.energystoragesubsystem &&
                    (!this.energystoragesubsystem.childGroups || this.energystoragesubsystem.childGroups.length === 0)
                );
            },
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            showDeleteIntegratedStorageButton() {
                return this.isIntegratedEnergyStorageSystem;
            },
            showDeleteInverterButton() {
                return this.isInverterAndBattery && this.energystoragesubsystem.childGroups.length <= 0;
            },
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        methods: {
            getEquipmentManufacturers(equipmentType) {
                const eqManufList =
                    this.$serviceControllers.EnergyStorageSystem.getEquipmentManufacturers(equipmentType);
                if (this.energystoragesubsystem && this.energystoragesubsystem.manufacturerId) {
                    const clearManufId = extendEquipmentManufList(
                        eqManufList,
                        this.energystoragesubsystem.manufacturerId,
                        this.energyStorageElement.projectElement.AuxiliaryModel.storedEquipmentModels
                    );

                    if (clearManufId) {
                        this.energyStorageElement.setParentItemProperty({
                            subsystemId: this.energystoragesubsystem.id,
                            name: "manufacturerId",
                            value: ""
                        });
                    }
                }

                return eqManufList;
            },
            async loadParentEquipmentModels(equipmentType, manufacturerName) {
                const equipmentModelList =
                    (await this.$serviceControllers.EnergyStorageSystem.getEquipmentModels(
                        equipmentType,
                        manufacturerName
                    )) || [];

                if (this.energystoragesubsystem && this.energystoragesubsystem.modelId) {
                    extendEquipmentModelList(
                        equipmentModelList,
                        this.energystoragesubsystem.modelId,
                        this.energyStorageElement.projectElement.AuxiliaryModel.storedEquipmentModels,
                        this.updatedModelSuffix
                    );
                }

                this.equipmentModels = equipmentModelList;

                return this.equipmentModels;
            },
            onAddEmptyBattery() {
                this.energyStorageElement.addEmptyChild(this.energystoragesubsystem.id, COMPONENT_TYPE.Battery);
            },
            onDelete() {
                this.energyStorageElement.deleteSubsystem(this.energystoragesubsystem.id);
            },
            async onCopyInvBattSubsystemClicked() {
                this.copyingInvBattSubsystem = true;
                await this.$app.timeout(0);
                this.$emit("copy-invbatt-subsystem", this.energystoragesubsystem.id);
                this.copyingInvBattSubsystem = false;
            },
            async onCopySubsystemParentClicked() {
                this.$emit("copy-subsystem-parent", this.energystoragesubsystem.id);
            },
            async copyEquipmentGroup(equipmentGroupId) {
                const newEquipmentId = await this.energyStorageElement.copyEquipmentGroup(
                    this.energystoragesubsystem.id,
                    equipmentGroupId
                );
                // Put in a break to allow the UI to update with the ref for the new module
                await this.$app.timeout(0);
                // Set the focus to the new equipment group
                if (this.$refs[newEquipmentId]) {
                    this.$refs[newEquipmentId][0].focusEquipmentGroupInput();
                }
            },
            focusIesSubsystemInput() {
                this.$refs.iesModelPicker.setFocusToQuantityInput();
            },
            focusInvBattSubsystemInput() {
                this.$refs.invBattModelPicker.setFocusToQuantityInput();
            },
            enter: SLIDE_DOWN_UP.enter,
            leave: SLIDE_DOWN_UP.leave
        }
    };
</script>
